import { autoinject, observable, child } from 'aurelia-framework';
import { ReportLinkAM, ReportService } from './report-api';
import { Router } from 'aurelia-router';
import { GlobalContextService } from '../../components/global-select/global-context-service';
import { ReportingStateService } from "./reporting-state-service";
import { EventAggregator } from 'aurelia-event-aggregator';
import { IDroppableHandler } from '../../components/drag-and-drop/models';
import { ResourcedNotificationService } from "../../components/notifications/notification-service";
import { ReportWordExportDialog } from "./report-word-export-dialog";

@autoinject
export class ReportList implements IDroppableHandler {
	@child("report-word-export-dialog") wordExportDialog: ReportWordExportDialog = new ReportWordExportDialog(this.api, this.reportingStateService, this.notificationService);
	
	@observable links: ReportLinkAM[];
	@observable ownLinks: ReportLinkAM[];
	@observable commonLinks: ReportLinkAM[];
	
	droppableHandler: IDroppableHandler;

	customerId: string;
	private editingPersonalFolder: boolean;
	private newPersonalFolderName: string;
	private editingCommonFolder: boolean;
	private newCommonFolderName: string;
	private editedFolderOriginalName: string;

	@observable draggedItem: ReportLinkAM;
	private dropTargetItem: ReportLinkAM;

	private commonRootFolder: ReportLinkAM = ReportList.createRootFolder(false);
	private privateRootFolder: ReportLinkAM = ReportList.createRootFolder(true);

	restoreState: boolean = false;
	lastViewedReportId: string = null;

	static createRootFolder(isPrivate: boolean) {
		const folder = new ReportLinkAM();
		folder.private = isPrivate;
		folder.reportId = isPrivate ? 'PrivateRootFolder' : 'CommonRootFolder'; // For getting drag target elements with getElementById()
		return folder;
	}

	constructor(
		private api: ReportService,
		private router: Router,
		private globalContext: GlobalContextService,
		private reportingStateService: ReportingStateService,
		private notificationService: ResourcedNotificationService,
		private eventAggregator: EventAggregator) {
		this.droppableHandler = this;
	}

	activate(urlParams: { restoreState: string }) {
		if (urlParams.restoreState == "1") {
			this.restoreState = true;
		}
	}

	async attached() {
		this.customerId = this.globalContext.getId('customer');

		await this.fetchReportLinks();

		this.handleUrlHashValue();

		if (this.restoreState) {
			// Restore report list state (opened folders, scroll position)
			const storedState = this.reportingStateService.getReportListState();
			if (storedState) {
				storedState.openFolders.forEach(folder => {
					this.openFolder(folder);
				})
				
				if (storedState.scrollPosition != 0) {
					setTimeout(() => {
						window.scroll(0, storedState.scrollPosition);
					}, 10);
				}

				this.lastViewedReportId = storedState.lastViewedReportId;
			}
		} else {
			this.reportingStateService.removeReportListState();
		}
		
		this.reportingStateService.removeReportToExport();
		this.eventAggregator.subscribe(`idChanged-customer`, async id => {
			this.customerId = id;
			await this.fetchReportLinks();
			await this.wordExportDialog.fetchCustomerWordTemplates();
			this.reportingStateService.removeReportListState();
			this.reportingStateService.removeReportToExport();
		});
	}
	
	get privateNonRootItemIsDragged() {
		return this.ownLinks && this.ownLinks.some(l => l.isDragged === true && !!l.parentFolderId);
	}

	get commonNonRootItemIsDragged() {
		return this.commonLinks && this.commonLinks.some(l => l.isDragged === true && !!l.parentFolderId);
	}

	get privateRootFolderId() {
		return this.privateRootFolder.reportId;
	}

	get commonRootFolderId() {
		return this.commonRootFolder.reportId;
	}

	getFolderItems(folderId: string) {
		return this.links.filter(l => l.parentFolderId === folderId);
	}
	
	handleUrlHashValue() {
		const hash = document.location.hash;

		if (hash && hash.startsWith('#folder=')) {
			var folderId = hash.substring(8);
			
			const folder = this.links.find(f => f.reportId === folderId);
			if (folder) {
				this.openFolder(folder);
			}
		}
	}

	async fetchReportLinks() {
		if (this.customerId != null) {
			const result = await this.api.getReportLinks(this.customerId);

			this.links = result.commonLinks.concat(result.privateLinks);
			this.commonLinks = result.commonLinks; 
			this.ownLinks = result.privateLinks;

			this.links.forEach(link => {
				link.isVisible = link.hierarchyLevel === 0;
			});
		}
	}
	
	addPersonalFolderClicked() {
		this.editingPersonalFolder = true;		
	}

	cancelAddPersonalFolderClicked() {
		this.editingPersonalFolder = false;
	}

	addCommonFolderClicked() {
		this.editingCommonFolder = true;		
	}

	cancelAddCommonFolderClicked() {
		this.editingCommonFolder = false;
	}
	
	validateUniqueFolderName(newValue: string, oldValue: string, isPersonal: boolean) {
		var links = isPersonal ? this.ownLinks : this.commonLinks;
		return newValue === oldValue || !links.some(l => l.isFolder && l.name === newValue);
	}

	async addNewFolder(name: string, isPersonal: boolean) {
		if (this.customerId != null && name && name.length > 0) {
			await this.api.createReportFolder(this.customerId, name, isPersonal);
			
			if (isPersonal) {
				this.newPersonalFolderName = null;
				this.editingPersonalFolder = false;
			}
			else {
				this.newCommonFolderName = null;
				this.editingCommonFolder = false;
			}

			await this.fetchReportLinks();
		}
	}

	async folderClicked(folder: ReportLinkAM) {
		if (folder.isBeingEdited) { return false; }
		
		folder.isOpen = !folder.isOpen;
		this.setLinkHierarchyVisibility(folder.reportId, folder.isOpen);
	}

	async updateFolderName(folder: ReportLinkAM, newName: string) {
		if (newName && newName.length && newName !== folder.name) {
			await this.api.renameFolder(folder.reportId, newName);
		}

		return false;
	}

	async deleteFolder(folder: ReportLinkAM) {
		if (folder.hasChildItems) { return false; }

		const collection = folder.private ? this.ownLinks : this.commonLinks;
		const parentFolderId = folder.parentFolderId;

		await this.api.deleteFolder(folder.reportId);

		this.removeReportLinkFromCollection(folder, this.links);
		this.removeReportLinkFromCollection(folder, collection);

		this.recalculateHasChildren(parentFolderId, collection);
	}

	private removeReportLinkFromCollection(link: ReportLinkAM, collection: ReportLinkAM[]) {
		const index = collection.findIndex(rl => rl.reportId === link.reportId);
		collection.splice(index, 1);
	}

	private recalculateHasChildren(folderId: string, collection: ReportLinkAM[]) {
		if (!folderId) { return; }

		const folder = collection.find(l => l.reportId === folderId);
		folder.hasChildItems = !!collection.find(l => l.parentFolderId === folderId);
	}

	private setLinkHierarchyVisibility(folderId: string, isVisible: boolean) {
		this.links.forEach(link => {
			if (link.parentFolderId === folderId) {
				link.isVisible = isVisible;
				if (link.isFolder) {
					if (!isVisible || link.isOpen) {
						this.setLinkHierarchyVisibility(link.reportId, isVisible);
					}
				}
			}
		});
	}

	private openFolder(folder: ReportLinkAM) {
		this.links.forEach(link => {

			if (link.reportId === folder.reportId) {
				link.isVisible = true;
				link.isOpen = true;
			}

			if (link.parentFolderId === folder.reportId) {
				link.isVisible = true;
			}

			if (folder.parentFolderId && link.reportId === folder.parentFolderId) {
				this.openFolder(link);
			}
		});
	}

	private getTargetDropSiteElement(targetItem: ReportLinkAM): HTMLElement {
		if (this.draggedItem.private == true) {
			return document.getElementById(`${targetItem.reportId}-own-drop-site`);
		} else {
			return document.getElementById(`${targetItem.reportId}-common-drop-site`);
		}
	}

	onReportItemMouseDown(event: any, item: ReportLinkAM) {
		// Prevent dragging of untargeted financial entries
		if (!item.reportId) { 
			return false; 
		}

		this.draggedItem = item;
		return true;
	}

	storeReportListState(item: ReportLinkAM) {
		// Store current list state to context
		const openFolders = this.links.filter(link => link.isFolder && link.isOpen);
		this.reportingStateService.setReportListState({ openFolders, lastViewedReportId: item.reportId, scrollPosition: window.scrollY });
	}

	handleDragEnter(event: any, item: ReportLinkAM) {
		this.dropTargetItem = item;
		return true;
	}

	onDragEnter(event: any, targetItem: ReportLinkAM) {
		if (event.preventDefault) {
			event.preventDefault(); // Necessary. Allows us to drop.
		}

		if (this.draggedItem && !this.draggedItem.reportId) {
			return false;
		}

		if (this.draggedItem && this.dropTargetItem && this.draggedItem.private !== this.dropTargetItem.private) {
			return false;
		}

		const dropSiteElement = this.getTargetDropSiteElement(targetItem);

		if (!dropSiteElement) {
			return false;
		}

		event.dataTransfer.dropEffect = 'move';
		dropSiteElement.classList.toggle('drag-enter');

		return false;
	}

	onDragLeave(event: any, targetItem: ReportLinkAM): void {
		this.dropTargetItem = null;

		const dropSiteElement = this.getTargetDropSiteElement(targetItem);

		if (dropSiteElement) {
			dropSiteElement.classList.toggle('drag-enter');
		}
	}

	async onDrop(event, targetItem: ReportLinkAM) {		
		event.stopPropagation(); // stops the browser from redirecting.

		const dropSiteElement = this.getTargetDropSiteElement(targetItem);
		dropSiteElement.classList.toggle('drag-enter');

		if (!this.draggedItem && !this.draggedItem.reportId) { 
			return false;
		}

		// No sorting on root level items
		if (!targetItem.isFolder && !this.draggedItem.parentFolderId && !targetItem.parentFolderId) {
			return false;
		}

		const jsonData = event.dataTransfer.getData('application/json');
		const data = <ReportLinkAM>JSON.parse(jsonData);
		
		if (!targetItem || targetItem.reportId === data.reportId) {
			return false;
		}
		const item = this.links.find(o => o.reportId === data.reportId);

		if (item.private !== targetItem.private) { return false; }

		if (item.isFolder) {
			if (item.reportId === targetItem.reportId) {
				return false;
			}
			const targetId = targetItem.isFolder ? targetItem.reportId : targetItem.parentFolderId;
			await this.api.moveFolderToParentFolder(item.reportId, targetId);
		}
		else {
			const targetId = targetItem.isFolder ? targetItem.reportId : targetItem.parentFolderId;

			// If targetItem is in root folder set target sort order to null
			const targetSortOrder = targetItem.parentFolderId ? targetItem.sortOrder : null;

			await this.api.moveReportToFolder(item.reportId, targetId, targetSortOrder);
		}

		await this.fetchReportLinks();
		
		this.openFolder(targetItem);

		return false;
	}
	
	openExportDialog(link: ReportLinkAM) {
		this.reportingStateService.setReportToExport({
			name: link.name,
			id: link.reportId,
			isFolder: link.isFolder
		});
		this.wordExportDialog.openDialog();
	}
	
}
